import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, Image, X, Plus, Search, Video, Images } from 'lucide-react';
import { SelectTags, type NewTagState } from '../../Tags/SelectTags';
import { deriveTagStateFromSource, flattenTagStateToNames } from '../../Tags/tagStateUtils';
import { userService } from '../../../services/userService';
import { useToast } from '../../UI/Toast';
import type { WorkCollaborator, WorkSubmitMode, WorkVisibility } from '../../../services/postService';

// Keep in sync with FILE_SIZE_LIMITS in
// GenAIPlatformWebAppService/src/services/mediaService.ts.
const PUBLISH_VIDEO_MAX_BYTES = 500 * 1024 * 1024;  // 500MB
const PUBLISH_IMAGE_MAX_BYTES = 50 * 1024 * 1024;   // 50MB

interface WorkFormProps {
    initialData?: {
        videoUrl?: string;
        thumbnailUrl?: string;
        title: string;
        description: string;
        tags: string[];
        collaborators?: WorkCollaborator[];
        workType?: 'video' | 'image';
        imageUrls?: string[];
        visibility?: WorkVisibility;
    };
    mode?: 'create' | 'edit';
    onSubmit: (data: any, submitMode: WorkSubmitMode) => Promise<void>;
    onCancel?: () => void;
    isSubmitting?: boolean;
    submitMode?: WorkSubmitMode | null;
    formId?: string;
    onDirtyChange?: (dirty: boolean) => void;
    onDataChange?: (data: any) => void;
}

const WorkForm: React.FC<WorkFormProps> = ({
    initialData,
    mode = 'create',
    onSubmit,
    onCancel,
    isSubmitting = false,
    submitMode = null,
    formId = 'work-form',
    onDirtyChange,
    onDataChange
}) => {
    const { showToast } = useToast();
    const videoInputRef = useRef<HTMLInputElement>(null);
    const coverInputRef = useRef<HTMLInputElement>(null);
    const imagesInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [workType, setWorkType] = useState<'video' | 'image'>(initialData?.workType || 'video');

    // Video State
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [videoPreview, setVideoPreview] = useState<string>(initialData?.videoUrl || '');

    // Image Set State
    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>(initialData?.imageUrls || []);

    // Common State
    const [coverFile, setCoverFile] = useState<File | null>(null);
    const [coverPreview, setCoverPreview] = useState<string>(initialData?.thumbnailUrl || '');
    const [title, setTitle] = useState(initialData?.title || '');
    const [description, setDescription] = useState(initialData?.description || '');

    // Layered tags state
    const [tagState, setTagState] = useState<NewTagState>({
        primaryCategory: null,
        secondaryCategories: [],
        toolTags: [],
        styleFacets: [],
    });

    // Initialize tags from initialData
    useEffect(() => {
        setTagState(deriveTagStateFromSource(initialData));
    }, [initialData]);

    const [collaboratorOptions, setCollaboratorOptions] = useState<WorkCollaborator[]>([]);
    const [isCollaboratorSearchLoading, setIsCollaboratorSearchLoading] = useState(false);
    const [followingCollaborators, setFollowingCollaborators] = useState<WorkCollaborator[]>([]);
    const [selectedCollaborators, setSelectedCollaborators] = useState<WorkCollaborator[]>(
        initialData?.collaborators || []
    );
    const [collaboratorSearch, setCollaboratorSearch] = useState('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        setWorkType(initialData?.workType || 'video');
        setVideoFile(null);
        setVideoPreview(initialData?.videoUrl || '');
        setImageFiles([]);
        setImagePreviews(initialData?.imageUrls || []);
        setCoverFile(null);
        setCoverPreview(initialData?.thumbnailUrl || '');
        setTitle(initialData?.title || '');
        setDescription(initialData?.description || '');
        setSelectedCollaborators(initialData?.collaborators || []);
        setCollaboratorSearch('');
        setCollaboratorOptions([]);
    }, [initialData]);

    // Video upload handler
    const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (mode === 'edit') return; // Should be disabled anyway
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > PUBLISH_VIDEO_MAX_BYTES) {
                showToast(`视频大小不能超过 500MB（当前 ${(file.size / (1024 * 1024)).toFixed(1)}MB）`, 'error');
                if (videoInputRef.current) videoInputRef.current.value = '';
                return;
            }
            setVideoFile(file);
            const url = URL.createObjectURL(file);
            setVideoPreview(url);

            if (!coverPreview) {
                const video = document.createElement('video');
                video.src = url;
                video.onloadeddata = () => { video.currentTime = 0; };
                video.onseeked = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(video, 0, 0);
                        setCoverFile(null);
                        setCoverPreview(canvas.toDataURL('image/jpeg'));
                    }
                };
            }
        }
    };

    // Cover upload handler
    const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setCoverFile(file);
            setCoverPreview(URL.createObjectURL(file));
        }
    };

    // Images Set upload handler
    const handleImagesUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (mode === 'edit') return;
        const files = Array.from(e.target.files || []);
        const oversize = files.find(f => f.size > PUBLISH_IMAGE_MAX_BYTES);
        if (oversize) {
            showToast(`单张图片不能超过 50MB（${oversize.name} 为 ${(oversize.size / (1024 * 1024)).toFixed(1)}MB）`, 'error');
            if (imagesInputRef.current) imagesInputRef.current.value = '';
            return;
        }
        if (files.length > 0) {
            setImageFiles(prev => [...prev, ...files]);

            const newPreviews = files.map(file => URL.createObjectURL(file));
            setImagePreviews(prev => {
                const updated = [...prev, ...newPreviews];
                // Automatically set cover if not set
                if (!coverPreview && updated.length > 0) {
                    setCoverPreview(updated[0]);
                }
                return updated;
            });
        }
        // clear input value so picking the same files again works
        if (imagesInputRef.current) imagesInputRef.current.value = '';
    };

    const removeImage = (indexToRemove: number) => {
        const removedPreview = imagePreviews[indexToRemove];
        const nextPreviews = imagePreviews.filter((_, index) => index !== indexToRemove);

        setImagePreviews(nextPreviews);
        setImageFiles(prev => prev.filter((_, index) => index !== indexToRemove));

        if (removedPreview === coverPreview) {
            setCoverPreview(nextPreviews[0] || '');
        }
    };



    useEffect(() => {
        let isCancelled = false;

        const loadFollowingCollaborators = async () => {
            const profileResponse = await userService.getUserProfile({ suppressAuthPrompt: true });
            const currentUserId = profileResponse.data?.userProfile?.userId;
            if (!profileResponse.success || !currentUserId || isCancelled) {
                return;
            }

            const response = await userService.getFollowing(currentUserId, { suppressAuthPrompt: true });
            if (isCancelled) {
                return;
            }

            setFollowingCollaborators(
                (response.following || [])
                    .map((user: any) => {
                        const id = String(user.userId || user.id || '');
                        return {
                            id,
                            name: user.displayName || user.nickname || user.name || user.username || id,
                            avatar: user.avatarUrl || user.avatar || '',
                            username: user.username || user.handle,
                            verified: user.verified
                        };
                    })
                    .filter((user: WorkCollaborator) => Boolean(user.id))
            );
        };

        void loadFollowingCollaborators();

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        const keyword = collaboratorSearch.trim();
        if (!keyword) {
            setCollaboratorOptions([]);
            setIsCollaboratorSearchLoading(false);
            return;
        }

        let isCancelled = false;
        const timer = window.setTimeout(async () => {
            setIsCollaboratorSearchLoading(true);
            const normalizedKeyword = keyword.toLowerCase();
            const matchesKeyword = (collaborator: WorkCollaborator) => [
                collaborator.name,
                collaborator.username,
                collaborator.id
            ].some(value => String(value || '').toLowerCase().includes(normalizedKeyword));
            const followedMatches = followingCollaborators.filter(matchesKeyword);
            const response = await userService.searchUsers(keyword, 8);
            if (isCancelled) {
                return;
            }

            if (response.success && response.data?.users) {
                const mergedCollaborators = new Map<string, WorkCollaborator>();

                followedMatches.forEach((collaborator) => {
                    mergedCollaborators.set(collaborator.id, collaborator);
                });

                response.data.users.forEach((user) => {
                    const collaborator = {
                        id: user.userId,
                        name: user.displayName || user.username || user.userId,
                        avatar: user.avatarUrl || '',
                        username: user.username,
                        verified: user.verified
                    };
                    if (!mergedCollaborators.has(collaborator.id)) {
                        mergedCollaborators.set(collaborator.id, collaborator);
                    }
                });

                setCollaboratorOptions(Array.from(mergedCollaborators.values()).slice(0, 8));
            } else {
                setCollaboratorOptions(followedMatches.slice(0, 8));
            }

            setIsCollaboratorSearchLoading(false);
        }, 250);

        return () => {
            isCancelled = true;
            window.clearTimeout(timer);
        };
    }, [collaboratorSearch, followingCollaborators]);

    const addCollaborator = (collaborator: WorkCollaborator) => {
        if (!selectedCollaborators.find(c => c.id === collaborator.id)) {
            setSelectedCollaborators(prev => [...prev, collaborator]);
        }
        setCollaboratorSearch('');
        setCollaboratorOptions([]);
    };

    const removeCollaborator = (id: string) => {
        setSelectedCollaborators(prev => prev.filter(c => c.id !== id));
    };

    const filteredCollaborators = collaboratorOptions.filter(c =>
        !selectedCollaborators.find(sc => sc.id === c.id)
    );

    const isMediaReady = mode === 'edit'
        ? true
        : workType === 'video'
            ? !!videoPreview
            : imagePreviews.length > 0;

    const buildSubmitData = useCallback(() => {
        const tags = flattenTagStateToNames(tagState);
        const categories = [tagState.primaryCategory, ...tagState.secondaryCategories].filter(Boolean) as string[];

        return {
            videoFile,
            videoPreview,
            imageFiles,
            imagePreviews,
            coverFile,
            coverPreview,
            coverImageIndex: workType === 'image' ? imagePreviews.findIndex((preview) => preview === coverPreview) : undefined,
            title,
            description,
            tags,
            categories,
            collaborators: selectedCollaborators,
            workType
        };
    }, [
        videoFile,
        videoPreview,
        imageFiles,
        imagePreviews,
        coverFile,
        coverPreview,
        title,
        description,
        tagState,
        selectedCollaborators,
        workType
    ]);

    useEffect(() => {
        onDataChange?.(buildSubmitData());
    }, [buildSubmitData, onDataChange]);

    useEffect(() => {
        const initialCollaborators = (initialData?.collaborators || []).map(item => item.id).sort();
        const currentCollaborators = selectedCollaborators.map(item => item.id).sort();

        const initialSnapshot = JSON.stringify({
            workType: initialData?.workType || 'video',
            title: initialData?.title || '',
            description: initialData?.description || '',
            tags: [...(initialData?.tags || [])].sort(),
            collaborators: initialCollaborators,
            videoPreview: initialData?.videoUrl || '',
            imagePreviews: initialData?.imageUrls || [],
            coverPreview: initialData?.thumbnailUrl || '',
            hasVideoFile: false,
            imageFileCount: 0,
            hasCoverFile: false
        });

        const currentTags = [...(buildSubmitData().tags || [])].sort();

        const currentSnapshot = JSON.stringify({
            workType,
            title,
            description,
            tags: currentTags,
            collaborators: currentCollaborators,
            videoPreview,
            imagePreviews,
            coverPreview,
            hasVideoFile: !!videoFile,
            imageFileCount: imageFiles.length,
            hasCoverFile: !!coverFile
        });

        const isDirty = initialSnapshot !== currentSnapshot;
        setHasUnsavedChanges(isDirty);
        onDirtyChange?.(isDirty);
    }, [
        initialData,
        workType,
        title,
        description,
        tagState,
        selectedCollaborators,
        videoPreview,
        imagePreviews,
        coverPreview,
        videoFile,
        imageFiles,
        coverFile,
        onDirtyChange
    ]);

    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
        const nextSubmitMode = (submitter?.dataset.submitMode as WorkSubmitMode | undefined) || 'publish';
        onSubmit(buildSubmitData(), nextSubmitMode);
    };

    return (
        <form id={formId} onSubmit={handleFormSubmit} className="space-y-5">
            {/* 媒体上传类型切换 & 区域 */}
            <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <Upload className="w-5 h-5 text-blue-500" />
                        {mode === 'edit' ? '媒体内容（不可修改）' : '上传媒体内容'} <span className="text-red-500">*</span>
                    </h2>

                    {/* 切换组件 */}
                    {mode === 'create' && (
                        <div className="flex bg-gray-100 p-1 rounded-lg">
                            <button
                                type="button"
                                onClick={() => setWorkType('video')}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${workType === 'video'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                <Video className="w-4 h-4" />
                                视频内容
                            </button>
                            <button
                                type="button"
                                onClick={() => setWorkType('image')}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${workType === 'image'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                <Images className="w-4 h-4" />
                                图集作品
                            </button>
                        </div>
                    )}
                </div>

                <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
                <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
                <input ref={imagesInputRef} type="file" multiple accept="image/*" onChange={handleImagesUpload} className="hidden" />

                <div className="flex gap-4">
                    {/* 上传区域 */}
                    <div className="flex-1">
                        {workType === 'video' ? (
                            videoPreview ? (
                                <div className="relative aspect-video bg-black rounded-lg overflow-hidden border border-gray-100 shadow-sm">
                                    <video src={videoPreview} controls className="w-full h-full object-contain" />
                                    {mode === 'create' && (
                                        <button
                                            type="button"
                                            onClick={() => { setVideoFile(null); setVideoPreview(''); setCoverPreview(''); }}
                                            className="absolute top-2 right-2 w-7 h-7 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors shadow-sm"
                                        >
                                            <X className="w-3.5 h-3.5 text-white" />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div
                                    onClick={() => mode === 'create' && videoInputRef.current?.click()}
                                    className={`aspect-video border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center ${mode === 'create' ? 'cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors' : ''}`}
                                >
                                    <Upload className="w-8 h-8 text-gray-300 mb-3" />
                                    <p className="text-gray-600 text-base font-medium">点击上传视频</p>
                                    <p className="text-gray-400 text-sm mt-1">支持 MP4, MOV, WebM 格式，最大 500MB</p>
                                </div>
                            )
                        ) : (
                            // Image Set UI
                            <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg p-4 min-h-[14rem]">
                                {imagePreviews.length > 0 ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {imagePreviews.map((preview, idx) => (
                                            <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-gray-100 shadow-sm group bg-white">
                                                <img src={preview} alt={`upload-${idx}`} className="w-full h-full object-cover" />

                                                {/* Set as Cover indicator */}
                                                {preview === coverPreview && (
                                                    <div className="absolute top-2 left-2 bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded shadow">
                                                        封面
                                                    </div>
                                                )}

                                                {/* Overlays on hover */}
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    {mode === 'create' && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); removeImage(idx); }}
                                                                className="absolute top-2 right-2 w-6 h-6 bg-red-500/90 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow"
                                                            >
                                                                <X className="w-3.5 h-3.5" />
                                                            </button>

                                                            {preview !== coverPreview && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => { e.stopPropagation(); setCoverPreview(preview); }}
                                                                    className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-white/90 text-gray-800 text-xs px-2 py-1 rounded shadow-sm font-medium hover:bg-white transition-colors whitespace-nowrap"
                                                                >
                                                                    设为封面
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                        {/* Append more images button */}
                                        {mode === 'create' && (
                                            <div
                                                onClick={() => imagesInputRef.current?.click()}
                                                className="aspect-square border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 hover:text-blue-500 transition-colors text-gray-400 bg-white"
                                            >
                                                <Plus className="w-6 h-6 mb-1" />
                                                <span className="text-xs font-medium">继续添加</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        onClick={() => mode === 'create' && imagesInputRef.current?.click()}
                                        className={`h-full flex flex-col items-center justify-center ${mode === 'create' ? 'cursor-pointer hover:text-blue-500' : ''}`}
                                    >
                                        <Images className="w-8 h-8 text-gray-300 mb-3" />
                                        <p className="text-gray-600 text-base font-medium">点击上传图集</p>
                                        <p className="text-gray-400 text-sm mt-1">支持 JPG, PNG, WEBP 格式，单张最大 50MB</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* 封面区域 (仅视频模式需要独立封面上传) */}
                    {workType === 'video' && (
                        <div className="w-44 flex flex-col">
                            <p className="text-sm text-gray-600 mb-2 flex items-center justify-between">
                                <span className="flex items-center gap-1 font-medium"><Image className="w-4 h-4" /> 封面</span>
                                {videoPreview && coverPreview && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => coverInputRef.current?.click()}
                                            className="text-blue-500 hover:text-blue-600 text-xs font-semibold"
                                        >
                                            更换
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const video = document.createElement('video');
                                                video.src = videoPreview;
                                                video.onloadeddata = () => { video.currentTime = 0; };
                                                video.onseeked = () => {
                                                    const canvas = document.createElement('canvas');
                                                    canvas.width = video.videoWidth;
                                                    canvas.height = video.videoHeight;
                                                    const ctx = canvas.getContext('2d');
                                                    if (ctx) {
                                                        ctx.drawImage(video, 0, 0);
                                                        setCoverFile(null);
                                                        setCoverPreview(canvas.toDataURL('image/jpeg'));
                                                    }
                                                };
                                            }}
                                            className="text-gray-400 hover:text-gray-600 text-xs font-semibold border-l border-gray-200 pl-2"
                                        >
                                            默认
                                        </button>
                                    </div>
                                )}
                            </p>

                            <div className="flex-1 min-h-[120px]">
                                {coverPreview ? (
                                    <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-gray-100 group shadow-sm">
                                        <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                                            <button
                                                type="button"
                                                onClick={() => coverInputRef.current?.click()}
                                                className="text-white text-xs bg-white/20 px-2.5 py-1 rounded border border-white/30 hover:bg-white/30 transition-colors"
                                            >
                                                更换封面
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        onClick={() => coverInputRef.current?.click()}
                                        className="w-full h-full border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-all font-medium"
                                    >
                                        <Plus className="w-6 h-6 text-gray-200" />
                                        <span className="text-gray-400 text-sm mt-1">设置封面</span>
                                    </div>
                                )}
                            </div>
                            {videoPreview && (
                                <p className="text-xs text-gray-400 mt-2 leading-snug">
                                    系统已为您自动抓取首帧作为默认封面
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* 标题和描述 */}
            <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            标题 <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="给作品起个标题"
                            maxLength={50}
                            className="w-full h-11 px-4 text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-400 bg-gray-50/30 transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">作品描述</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="描述你的创作过程、灵感、或者是使用的模型参数..."
                            rows={4}
                            maxLength={500}
                            className="w-full px-4 py-3 text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-400 transition-all bg-gray-50/30 min-h-[120px] resize-y"
                        />
                        <div className="flex justify-end mt-1">
                            <span className="text-xs text-gray-400">{description.length}/500</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* 标签 */}
            <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="pt-2">
                    <SelectTags
                        value={tagState}
                        onChange={(next) => {
                            setTagState(next);
                            setHasUnsavedChanges(true);
                        }}
                        showCollapsed={false}
                    />
                </div>
            </section>

            {/* 关联创作者 */}
            <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">关联创作者</h3>

                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={collaboratorSearch}
                        onChange={(e) => setCollaboratorSearch(e.target.value)}
                        placeholder="搜索并关联其他创作者..."
                        className="w-full h-11 pl-10 pr-4 text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-400 bg-gray-50/30 transition-all font-medium"
                    />

                    {collaboratorSearch && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20">
                            {isCollaboratorSearchLoading ? (
                                <div className="px-4 py-3 text-sm text-gray-500">搜索中...</div>
                            ) : filteredCollaborators.length > 0 ? (
                                filteredCollaborators.map(c => (
                                    <div
                                        key={c.id}
                                        onClick={() => addCollaborator(c)}
                                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        {c.avatar ? (
                                            <img src={c.avatar} alt={c.name} className="w-10 h-10 rounded-full object-cover shadow-sm" />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600">
                                                {c.name.slice(0, 1).toUpperCase()}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="text-base font-medium text-gray-900 truncate">{c.name}</p>
                                            <p className="text-sm text-gray-500 truncate">@{c.username || c.id} · ID: {c.id}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="px-4 py-3 text-sm text-gray-500">
                                    未找到匹配用户，请输入昵称、用户名或用户 ID
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {selectedCollaborators.length > 0 && (
                    <div className="flex flex-wrap gap-2.5 mt-4">
                        {selectedCollaborators.map(c => (
                            <div key={c.id} className="flex items-center gap-2 pl-1 pr-3 py-1 bg-gray-100 rounded-full group hover:bg-gray-200 transition-colors">
                                <img src={c.avatar} alt={c.name} className="w-7 h-7 rounded-full object-cover border border-white shadow-sm" />
                                <span className="text-sm font-medium text-gray-700">{c.name}</span>
                                <button type="button" onClick={() => removeCollaborator(c.id)} className="w-5 h-5 bg-gray-300 group-hover:bg-gray-400 rounded-full flex items-center justify-center transition-colors">
                                    <X className="w-3 h-3 text-white" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* 提交按钮区域 (如果是 Modal 模式) */}
            {onCancel && (
                <div className="flex justify-end gap-3 pt-4">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-6 py-2 rounded-full border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                    >
                        取消
                    </button>
                    <button
                        type="submit"
                        data-submit-mode="draft"
                        disabled={isSubmitting || !title || !isMediaReady || !hasUnsavedChanges}
                        className={`px-8 py-2 rounded-full font-medium transition-all ${isSubmitting || !title || !isMediaReady || !hasUnsavedChanges
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                    >
                        {submitMode === 'draft' ? '保存中...' : '保存'}
                    </button>
                    <button
                        type="submit"
                        data-submit-mode="publish"
                        disabled={isSubmitting || !title || !isMediaReady}
                        className={`px-8 py-2 rounded-full font-medium transition-all ${isSubmitting || !title || !isMediaReady
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-black text-white hover:bg-gray-800'
                            }`}
                    >
                        {submitMode === 'publish' ? '发布中...' : '发布'}
                    </button>
                </div>
            )}
        </form>
    );
};

export default WorkForm;
